import { c as _c } from "react/compiler-runtime";
import { ArrowRight, Laurel, SuitcaseSolid } from '@byway/icons';
import { clsx } from 'clsx';
import { parseISO } from 'date-fns';
import { useFormatter, useTranslations } from 'next-intl';
import type { CSSProperties } from 'react';
import { Link } from '~/components/Link/src/Link';
import type { LoyaltyCustomer } from '~/services/loyalty';
import styles from './RewardsTierBanner.module.css';
import { REWARDS_TIERS } from './RewardsTiers.config';
interface RewardsTierBannerProps {
  readonly customer: LoyaltyCustomer;
  readonly tripsBooked?: number;
}
const TIMELINE_DOTS = 5;
const LABEL_DOT_BY_TIER_ORDER = [0, 2, 4];
export function RewardsTierBanner(t0) {
  const $ = _c(55);
  const {
    customer,
    tripsBooked
  } = t0;
  const t = useTranslations("account");
  const tCommon = useTranslations("common");
  const format = useFormatter();
  const currentIndex = REWARDS_TIERS.findIndex(tier => tier.tierId === customer.tierId);
  const currentTier = currentIndex >= 0 ? REWARDS_TIERS[currentIndex] : undefined;
  const nextTier = currentIndex >= 0 ? REWARDS_TIERS[currentIndex + 1] : undefined;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== currentTier || $[1] !== customer.tier || $[2] !== customer.tierStatus || $[3] !== customer.tripsToMaintainTier || $[4] !== customer.tripsToMaintainTierDate || $[5] !== customer.tripsToNextTier || $[6] !== customer.tripsToNextTierDate || $[7] !== format || $[8] !== nextTier || $[9] !== t || $[10] !== tCommon || $[11] !== tripsBooked) {
    const currentTierName = currentTier ? t(currentTier.nameKey) : customer.tier;
    const nextTierName = nextTier ? t(nextTier.nameKey) : "";
    let t9;
    if ($[20] !== format) {
      t9 = iso => iso ? format.dateTime(parseISO(iso), {
        day: "numeric",
        month: "long",
        year: "numeric"
      }) : "";
      $[20] = format;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    const formatDate = t9;
    const remaining = customer.tripsToNextTier ?? 0;
    const levelUpDate = formatDate(customer.tripsToNextTierDate);
    const maintainRemaining = customer.tripsToMaintainTier ?? 0;
    const maintainDate = formatDate(customer.tripsToMaintainTierDate);
    const showMaintain = !nextTier || customer.tierStatus !== "Active";
    const maintainText = maintainRemaining > 0 ? t("rewards_banner_cta_maintain", {
      remaining: maintainRemaining,
      date: maintainDate,
      tier: currentTierName
    }) : null;
    const ctaText = showMaintain ? maintainText : t("rewards_banner_cta_next", {
      remaining,
      date: levelUpDate,
      nextTier: nextTierName
    });
    const tripsDone = nextTier && customer.tripsToNextTier !== null ? nextTier.tripsRequired - customer.tripsToNextTier : currentTier?.tripsRequired ?? 0;
    const points = currentTier ? Math.min(Math.max(tripsDone, 0), TIMELINE_DOTS) : 0;
    const labelByDot = new Map(REWARDS_TIERS.map(_temp));
    const dots = Array.from({
      length: TIMELINE_DOTS
    }, (_, dot) => {
      const tier_1 = labelByDot.get(dot);
      return {
        key: tier_1?.id ?? `after-${labelByDot.get(dot - 1)?.id ?? dot}`,
        tier: tier_1,
        filled: dot < points
      };
    });
    const reachedFraction = Math.min(Math.max(points - 1, 0), TIMELINE_DOTS - 1) / (TIMELINE_DOTS - 1);
    t5 = styles.banner;
    let t10;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <Laurel decorative={true} size="scale" strokeWidth={1} className={styles.laurel} />;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    let t11;
    if ($[23] !== currentTierName) {
      t11 = <span>{currentTierName}</span>;
      $[23] = currentTierName;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    let t12;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Laurel decorative={true} size="scale" strokeWidth={1} className={clsx(styles.laurel, styles.laurelFlipped)} />;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    let t13;
    if ($[26] !== t11) {
      t13 = <p className={styles.tier}>{t10}{t11}{t12}</p>;
      $[26] = t11;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    let t14;
    if ($[28] !== t || $[29] !== tripsBooked) {
      t14 = tripsBooked !== undefined && <span className={styles.badge}>{t("rewards_banner_trips_badge", {
          count: tripsBooked
        })}</span>;
      $[28] = t;
      $[29] = tripsBooked;
      $[30] = t14;
    } else {
      t14 = $[30];
    }
    if ($[31] !== t13 || $[32] !== t14) {
      t6 = <div className={styles.identity}>{t13}{t14}</div>;
      $[31] = t13;
      $[32] = t14;
      $[33] = t6;
    } else {
      t6 = $[33];
    }
    let t15;
    if ($[34] !== currentTier || $[35] !== t) {
      t15 = currentTier && <span className={styles.benefits}>{t(currentTier.bannerBenefitsKey)}</span>;
      $[34] = currentTier;
      $[35] = t;
      $[36] = t15;
    } else {
      t15 = $[36];
    }
    t7 = <p className={styles.body}>{t15}{ctaText}</p>;
    let t16;
    if ($[37] !== tCommon) {
      t16 = tCommon("learn_more");
      $[37] = tCommon;
      $[38] = t16;
    } else {
      t16 = $[38];
    }
    let t17;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <ArrowRight decorative={true} size="small" />;
      $[39] = t17;
    } else {
      t17 = $[39];
    }
    if ($[40] !== t16) {
      t8 = <div className={styles.cta}><Link href="/account/rewards#rewards-tiers" asButton="secondary" isPill={true} isTransparent={true} size="responsive" noWrap={true}><span className={styles.ctaText}>{t16}{t17}</span></Link></div>;
      $[40] = t16;
      $[41] = t8;
    } else {
      t8 = $[41];
    }
    t1 = styles.timeline;
    let t18;
    if ($[42] !== reachedFraction) {
      t18 = {
        "--reached": reachedFraction
      };
      $[42] = reachedFraction;
      $[43] = t18;
    } else {
      t18 = $[43];
    }
    t2 = t18 as CSSProperties;
    t3 = showMaintain ? t("rewards_banner_progress_label_current", {
      tier: currentTierName,
      points,
      total: TIMELINE_DOTS
    }) : t("rewards_banner_progress_label", {
      points,
      total: TIMELINE_DOTS,
      nextTier: nextTierName
    });
    t4 = dots.map(t19 => {
      const {
        key,
        tier: tier_2,
        filled
      } = t19;
      return <li key={key} className={clsx(styles.step, filled ? styles.reached : styles.upcoming)}><span className={styles.dot}>{filled && <SuitcaseSolid decorative={true} className={styles.dotIcon} />}</span>{tier_2 && <span className={clsx(styles.label, tier_2.id === currentTier?.id && styles.labelCurrent)}>{t(tier_2.nameKey)}</span>}</li>;
    });
    $[0] = currentTier;
    $[1] = customer.tier;
    $[2] = customer.tierStatus;
    $[3] = customer.tripsToMaintainTier;
    $[4] = customer.tripsToMaintainTierDate;
    $[5] = customer.tripsToNextTier;
    $[6] = customer.tripsToNextTierDate;
    $[7] = format;
    $[8] = nextTier;
    $[9] = t;
    $[10] = tCommon;
    $[11] = tripsBooked;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t1 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
    t8 = $[19];
  }
  let t9;
  if ($[44] !== t1 || $[45] !== t2 || $[46] !== t3 || $[47] !== t4) {
    t9 = <ol className={t1} style={t2} aria-label={t3}>{t4}</ol>;
    $[44] = t1;
    $[45] = t2;
    $[46] = t3;
    $[47] = t4;
    $[48] = t9;
  } else {
    t9 = $[48];
  }
  let t10;
  if ($[49] !== t5 || $[50] !== t6 || $[51] !== t7 || $[52] !== t8 || $[53] !== t9) {
    t10 = <div className={t5}>{t6}{t7}{t8}{t9}</div>;
    $[49] = t5;
    $[50] = t6;
    $[51] = t7;
    $[52] = t8;
    $[53] = t9;
    $[54] = t10;
  } else {
    t10 = $[54];
  }
  return t10;
}
function _temp(tier_0, index) {
  return [LABEL_DOT_BY_TIER_ORDER[index], tier_0];
}